import { DateTime } from 'luxon';

export const formatToDateString = (utcString: string | DateTime): string => {
  let formattedString = '';
  if (utcString instanceof DateTime) {
    return utcString.toFormat('LL/dd/yyyy', { locale: 'utc' });
  } else {
    const dtInGMT = DateTime.fromISO(utcString);
    if (dtInGMT.isValid) {
      formattedString = dtInGMT.toFormat('LL/dd/yyyy', {
        locale: 'utc',
      });
    }
  }
  return formattedString;
};

export const formatToLongDateString = (
  utcString: string | DateTime
): string => {
  let formattedString = '';
  if (utcString instanceof DateTime) {
    return utcString.toLocaleString(DateTime.DATE_FULL, { locale: 'utc' });
  } else {
    const dtInGMT = DateTime.fromISO(utcString);
    if (dtInGMT.isValid) {
      formattedString = dtInGMT.toLocaleString(DateTime.DATE_FULL, {
        locale: 'utc',
      });
    }
  }
  return formattedString;
};

export const formatJSDateToDateString = (date?: Date): string => {
  let formattedString = '';
  if (date) {
    formattedString = date.toISOString().split('T')[0];
  }
  return formattedString;
};

export const formatToDateTimeString = (utcString: string): string => {
  let formattedString = '';
  const usCentralTimeZone = 'America/Chicago';
  if (utcString) {
    const dtInGMT = DateTime.fromISO(utcString, { setZone: true });
    if (dtInGMT.isValid) {
      formattedString = dtInGMT.setZone(usCentralTimeZone).toLocaleString({
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    }
  }

  return formattedString;
};

export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(date.getDate() + days);
  return result;
};

export const getAgeByDate = (birthdate: string, ageOnDate?: string): number => {
  const birthDate = DateTime.fromFormat(birthdate, 'yyyy-MM-dd');

  if (!birthDate.isValid) {
    return 0;
  }

  let targetDate: DateTime;
  if (ageOnDate) {
    targetDate = DateTime.fromFormat(ageOnDate, 'yyyy-MM-dd');
    if (!targetDate.isValid) {
      return 0;
    }
  } else {
    targetDate = DateTime.now();
  }

  let age = targetDate.year - birthDate.year;
  const targetMonthDay = targetDate.month * 100 + targetDate.day;
  const birthMonthDay = birthDate.month * 100 + birthDate.day;

  if (targetMonthDay < birthMonthDay) {
    age -= 1;
  }

  return Math.max(0, age);
};

export const formatDateToMMDDYYYY = (value: string): string => {
  if (!value || typeof value !== 'string') return value;
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : value;
};

export const formatDateToYYYYMMDD = (value: string): string => {
  if (!value || typeof value !== 'string') return value;
  const match = value.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return match ? `${match[3]}-${match[1]}-${match[2]}` : value;
};

const COMPLETE_TYPED_DATE_PATTERN = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;

export const formatTypedDateInput = (inputValue: string): string => {
  const digitsOnly = inputValue.replace(/\D/g, '');
  const isCompleteFormat = COMPLETE_TYPED_DATE_PATTERN.test(inputValue);
  const hasSeparators = /[/-]/.test(inputValue);
  const hasValidSeparatorPositions =
    /^\d{1,2}([/-]\d{0,2}([/-]\d{0,4})?)?$/.test(inputValue);

  if (
    digitsOnly.length === 0 ||
    isCompleteFormat ||
    (hasSeparators && hasValidSeparatorPositions)
  ) {
    return inputValue;
  }

  if (digitsOnly.length <= 2) {
    return digitsOnly;
  }
  if (digitsOnly.length === 3) {
    return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
  }
  if (digitsOnly.length === 4) {
    return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/`;
  }
  if (digitsOnly.length <= 8) {
    return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(
      2,
      4
    )}/${digitsOnly.slice(4)}`;
  }

  return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(
    2,
    4
  )}/${digitsOnly.slice(4, 8)}`;
};

export const isDateWithinAnnualWindow = (
  date: Date,
  startMonth: number,
  startDay: number,
  endMonth: number,
  endDay: number
): boolean => {
  const year = date.getFullYear();
  const windowStart = new Date(year, startMonth, startDay);
  const windowEnd = new Date(year, endMonth, endDay, 23, 59, 59, 999);
  return date >= windowStart && date <= windowEnd;
};
